import * as queryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Omit } from '../../../ts/helpers/TypescriptHelpers';
import { ExplorableItemType } from '../../../ts/models/ContentType';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '../../../ts/models/sort/SortOptionDefinitions';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace } from '../../actions/spaces/EnterSpaceAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { ServerTasksSubspaceName, serverTasksSubspaceNameToExplorableItemType, SiteTasksSubspaceName, siteTasksSubspaceNameToExplorableItemType, SpaceInfo, SpaceItemForNonEntitySpace } from '../../state/SpaceInfo';
import { UrlParamKeys } from '../../UrlParamKeys';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { getOrderClausesFromQueryParam } from '../Explorer/ExplorerUrlBindings';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { ConnectedTasksSpace } from './ConnectedTasksSpace';
import { TasksSpaceOwnProps } from './TasksSpace';

export interface TasksSpaceProviderOwnProps {
  readonly siteName: string;
  readonly subspaceName: SiteTasksSubspaceName | ServerTasksSubspaceName;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (props: TasksSpaceOwnProps) => JSX.Element;
  readonly inServerArea: boolean;
}

export interface TasksSpaceProviderStateProps {}

export interface TasksSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
}

export type TasksSpaceProviderContainerProps = TasksSpaceProviderOwnProps & TasksSpaceProviderStateProps & TasksSpaceProviderDispatchProps;

export interface TasksSpaceProviderState {}

export class TasksSpaceProviderContainer extends React.PureComponent<TasksSpaceProviderContainerProps, TasksSpaceProviderState> {

  public constructor(props: TasksSpaceProviderContainerProps) {
    super(props);
  }

  public render() {
    return this.renderSpace();
  }

  private renderSpace = () => {
    const props: TasksSpaceOwnProps = {
      urls: this.props.urls,
      inServerArea: this.props.inServerArea
    };
    return this.props.renderSpace(props);
  }

  public componentDidMount() {
    this.props.requestSpaceContents();
  }

  public componentDidUpdate(prevProps: TasksSpaceProviderContainerProps) {
    if(this.props.siteName !== prevProps.siteName) {
      this.changeSubspace(true);
    } else if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace(false);
    }
  }

  public componentWillUnmount() {}

  private changeSubspace(didSiteChange: boolean) {
    this.props.enterSpace(
      {
        item: SpaceItemForNonEntitySpace,
        spaceType: this.props.inServerArea ? 'servertasks' : 'sitetasks',
        subspaceName: this.props.subspaceName
      }
    );

    if(!didSiteChange) {
      // We are just switching tabs so we know content counts are up to date.
      this.props.contentCountsUpToDate();
    }

    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();
  }

  private clearAndSetSortOrderFromURL() {
    const {contentTypes, spaceInfo} = this.getContentTypesAndSpaceInfo();
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private getContentTypesAndSpaceInfo() {
    if (this.props.inServerArea) {
      return {
        contentTypes: [serverTasksSubspaceNameToExplorableItemType(this.props.subspaceName as ServerTasksSubspaceName) as ExplorableItemType],
        spaceInfo: {item: undefined, spaceType: 'servertasks', subspaceName: this.props.subspaceName} as SpaceInfo
      };
    } else {
      return {
        contentTypes: [siteTasksSubspaceNameToExplorableItemType(this.props.subspaceName as SiteTasksSubspaceName) as ExplorableItemType],
        spaceInfo: {item: undefined, spaceType: 'sitetasks', subspaceName: this.props.subspaceName} as SpaceInfo
      };
    }
  }
}


const mapDispatchToProps: MapDispatchToProps<TasksSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  requestSpaceContents,
  contentCountsUpToDate,
  setOrderAction
}, dispatch);

export const ConnectedTasksSpaceProviderContainer: React.ComponentClass<TasksSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(TasksSpaceProviderContainer);

const renderSpace = (props: TasksSpaceOwnProps): JSX.Element => <ConnectedTasksSpace {...props} />;

export interface LocationPathProp {
  readonly routeLocationPath: string;
}
export type TasksSpaceProviderProps = LocationPathProp & Omit<TasksSpaceProviderOwnProps, 'renderSpace'>;

export function TasksSpaceProvider(props: TasksSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: true,
    shouldUpdateContentTypes: false,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: SpaceItemForNonEntitySpace,
    spaceType: props.inServerArea ? 'servertasks' : 'sitetasks',
    subspaceName: props.subspaceName
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedTasksSpaceProviderContainer {...{...props, renderSpace}} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}
